import type { CSSProperties } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  fetchAdminAction,
  requestAdminAction,
  type ActionSummary,
  type AdminActionType,
} from "../../lib/admin/adminActions.mjs";
import {
  buildInvitePayload,
  buildRegenerateLinkPayload,
  buildSetPasswordLink,
  listTenantUsers,
  ROLE_OPTIONS,
  summarizeInviteResult,
  summarizeRegenerateResult,
  type InviteResultSummary,
  type RegenerateResultSummary,
  type TenantUserRole,
  type TenantUserRow,
} from "../../lib/admin/tenantUsers.mjs";
import { getSupabase } from "../../lib/supabase";
import ActionStatusPanel from "./ActionStatusPanel";

/**
 * MER-492 PR C: tenant user management for one customer.
 *
 * Reads come from the staff-gated admin_list_tenant_users RPC
 * (migration 0044); every write goes through the admin action queue
 * (requestAdminAction -> droplet executor, PR B) and renders an
 * inline ActionStatusPanel until the action is terminal.
 *
 * Invite outcomes (one-time link, "email sent") are hoisted to
 * panel-level state and rendered OUTSIDE the table: a list refresh
 * after a terminal action must never unmount the box holding a
 * one-time link that is shown exactly once. The link is never logged.
 * MER-539's regenerate outcome rides the same hoisted box, for the
 * same reason and with the same epoch gating.
 *
 * MER-539: the row actions split into two verbs that were one before.
 * "Resend invite" (pending members only) re-runs tenant_user_invite,
 * which tries the invite EMAIL first. "Regenerate login link" runs
 * tenant_user_regenerate_link, which never sends mail and always
 * returns a link for staff to hand over, and works for a member who
 * has already signed in (the invite path refuses those, which is why
 * Seth Heath's lockouts needed a hand-run script).
 *
 * Style constants mirror CustomerDetail.tsx (not exported there);
 * keep the two in sync by eye until a shared module exists.
 */

const ROLE_LABELS: Record<TenantUserRole, string> = {
  owner: "Owner",
  viewer: "Viewer",
};

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

function relativeTime(iso: string | null): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "Never";
  const delta = Date.now() - t;
  if (delta < 60 * 1000) return "just now";
  if (delta < ONE_HOUR_MS) {
    const mins = Math.max(1, Math.round(delta / (60 * 1000)));
    return `${mins}m ago`;
  }
  if (delta < ONE_DAY_MS) {
    return `${Math.round(delta / ONE_HOUR_MS)}h ago`;
  }
  return `${Math.round(delta / ONE_DAY_MS)}d ago`;
}

/** Same submit-state shape as CustomerDetail's useAdminAction, plus
 * lastType so terminal handlers know whether to fetch an invite
 * result row. */
function useAdminAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  const [lastType, setLastType] = useState<AdminActionType | null>(null);

  async function run(type: AdminActionType, payload: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    // Clear any prior action's panel up front: if this submit fails,
    // a stale status panel must not render beside the fresh error.
    setActionId(null);
    setLastType(type);
    try {
      const res = await requestAdminAction(getSupabase(), type, payload);
      setActionId(res.actionId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return { busy, error, actionId, lastType, run };
}

type UsersState =
  | { phase: "loading" }
  | { phase: "ready"; users: TenantUserRow[] }
  | { phase: "error"; message: string };

interface InviteOutcome {
  kind: "invite";
  email: string;
  summary: InviteResultSummary;
}

interface RegenerateOutcome {
  kind: "regenerate";
  email: string;
  summary: RegenerateResultSummary;
}

/** One hoisted box, two shapes. Both hold a one-time credential, so
 * both go through the same epoch-gated channel and the same
 * shown-once rendering. */
type PanelOutcome = InviteOutcome | RegenerateOutcome;

export default function UsersPanel(props: {
  tenantId: string;
  slug: string;
  accessCode?: string | null;
  onActionTerminal?: () => void;
}) {
  const { tenantId, slug, accessCode, onActionTerminal } = props;
  const [state, setState] = useState<UsersState>({ phase: "loading" });
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [showRevoked, setShowRevoked] = useState(false);
  // Hoisted invite outcome (see the component doc comment): one box,
  // stable across list refreshes, replaced when a newer invite-type
  // action lands and cleared when any new action is submitted
  // (generating a new link invalidates the prior one anyway).
  const [outcome, setOutcome] = useState<PanelOutcome | null>(null);
  // Epoch-gated publishing: a late fetchAdminAction resolving AFTER a
  // newer submission cleared the box must not resurrect a now-invalid
  // one-time link. Children capture the epoch at submission time and
  // publish through it; stale epochs are dropped.
  const outcomeEpochRef = useRef(0);
  const outcomeChannel = useMemo<OutcomeChannel>(
    () => ({
      clear: () => {
        outcomeEpochRef.current += 1;
        setOutcome(null);
        return outcomeEpochRef.current;
      },
      publish: (epoch, next) => {
        if (epoch === outcomeEpochRef.current) setOutcome(next);
      },
    }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    // Keep the previous list mounted during refreshes so per-row
    // status panels are not destroyed mid-poll; only the very first
    // load shows the loading state.
    setState((s) => (s.phase === "ready" ? s : { phase: "loading" }));
    listTenantUsers(getSupabase(), tenantId)
      .then((users) => {
        if (!cancelled) setState({ phase: "ready", users });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ phase: "error", message: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, refreshNonce]);

  function handleTerminal(succeeded: boolean) {
    onActionTerminal?.();
    if (succeeded) setRefreshNonce((n) => n + 1);
  }

  const users = state.phase === "ready" ? state.users : [];
  const activeUsers = users.filter((u) => !u.revokedAt);
  const revokedUsers = users.filter((u) => u.revokedAt);

  return (
    <section style={panelStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={panelTitleStyle}>Users</h2>
        <button
          type="button"
          onClick={() => setRefreshNonce((n) => n + 1)}
          style={smallSecondaryButtonStyle}
        >
          Refresh
        </button>
      </div>

      {state.phase === "loading" ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
          Loading users...
        </div>
      ) : null}
      {state.phase === "error" ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--flag-red)" }}>
          Could not load users: {state.message}
        </div>
      ) : null}

      {state.phase === "ready" ? (
        users.length === 0 ? (
          <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
            No users yet. Invite the first one below.
          </div>
        ) : (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Email</th>
                  <th style={headerCellStyle}>Role</th>
                  <th style={headerCellStyle}>Last login</th>
                  <th style={headerCellStyle}>Invited</th>
                  <th style={headerCellStyle}>Status</th>
                  <th style={headerCellStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeUsers.map((u) => (
                  <UserRow
                    key={u.userId}
                    user={u}
                    slug={slug}
                    onTerminal={handleTerminal}
                    outcomeChannel={outcomeChannel}
                  />
                ))}
                {showRevoked
                  ? revokedUsers.map((u) => (
                      <UserRow
                        key={u.userId}
                        user={u}
                        slug={slug}
                        onTerminal={handleTerminal}
                        outcomeChannel={outcomeChannel}
                      />
                    ))
                  : null}
              </tbody>
            </table>
            {revokedUsers.length > 0 ? (
              <div>
                <button
                  type="button"
                  onClick={() => setShowRevoked((v) => !v)}
                  style={smallSecondaryButtonStyle}
                >
                  {showRevoked
                    ? `Hide revoked (${revokedUsers.length})`
                    : `Show revoked (${revokedUsers.length})`}
                </button>
              </div>
            ) : null}
          </>
        )
      ) : null}

      <InviteBlock slug={slug} onTerminal={handleTerminal} outcomeChannel={outcomeChannel} />

      {outcome?.kind === "invite" ? (
        <InviteOutcomeBox email={outcome.email} summary={outcome.summary} accessCode={accessCode} />
      ) : null}
      {outcome?.kind === "regenerate" ? (
        <RegenerateOutcomeBox email={outcome.email} summary={outcome.summary} />
      ) : null}
    </section>
  );
}

type RowConfirm = "revoke" | "resend" | "regenerate" | "reinvite";

/** Epoch-gated channel for the hoisted outcome box. */
type OutcomeChannel = {
  clear: () => number;
  publish: (epoch: number, outcome: PanelOutcome) => void;
};

function UserRow(props: {
  user: TenantUserRow;
  slug: string;
  onTerminal: (succeeded: boolean) => void;
  outcomeChannel: OutcomeChannel;
}) {
  const { user, slug, onTerminal, outcomeChannel } = props;
  const action = useAdminAction();
  const [confirm, setConfirm] = useState<RowConfirm | null>(null);
  const [pendingRole, setPendingRole] = useState<TenantUserRole | null>(null);
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const submitEpochRef = useRef(0);

  const revoked = Boolean(user.revokedAt);
  const pendingInvite = !revoked && Boolean(user.invitedAt) && !user.lastSignInAt;
  const roleKnown = user.role === "owner" || user.role === "viewer";

  function runRow(type: AdminActionType, payload: Record<string, unknown>) {
    // Any new action obsoletes the displayed outcome (a fresh one-time
    // link invalidates the prior link anyway).
    submitEpochRef.current = outcomeChannel.clear();
    setPayloadError(null);
    void action.run(type, payload);
  }

  /** MER-539. The confirm flag is set HERE, from the click-through, and
   * only ever as the literal true: buildRegenerateLinkPayload refuses
   * anything else, the RPC compares against the jsonb literal, and the
   * executor re-checks with `is True`. It is the audit-log evidence
   * that a human confirmed minting this credential, so it must never
   * become a default. */
  function submitRegenerate() {
    const built = buildRegenerateLinkPayload({
      slug,
      userId: user.userId,
      confirmRegenerate: true,
    });
    if (!built.ok) {
      setPayloadError(Object.values(built.errors).join(" "));
      return;
    }
    runRow("tenant_user_regenerate_link", built.payload);
  }

  function handleSummary(s: ActionSummary) {
    if (!s.terminal) return;
    // Outcome boxes are driven off the TERMINAL summary, never off the
    // enqueue result: the executor runs on the droplet poller, so both
    // the credential and any guard rejection only exist once the action
    // reaches a terminal state (the MER-516 lesson).
    if (s.status === "succeeded" && action.actionId) {
      const email = user.email ?? "";
      const epoch = submitEpochRef.current;
      const type = action.lastType;
      if (type === "tenant_user_invite" || type === "tenant_user_regenerate_link") {
        fetchAdminAction(getSupabase(), action.actionId)
          .then((row) => {
            if (type === "tenant_user_invite") {
              outcomeChannel.publish(epoch, {
                kind: "invite",
                email,
                summary: summarizeInviteResult(row),
              });
              return;
            }
            const summary = summarizeRegenerateResult(row);
            // The executor reports the address it actually generated the
            // link for (read off the auth user). Prefer it over the row's
            // email so the box can never name a different person than the
            // link belongs to.
            outcomeChannel.publish(epoch, {
              kind: "regenerate",
              email: summary.email ?? email,
              summary,
            });
          })
          .catch(() => {
            // The link stays server-side only; staff can run Regenerate
            // login link again, which issues a fresh one and invalidates
            // whatever this action produced.
          });
      }
    }
    onTerminal(s.status === "succeeded");
  }

  const detailOpen =
    pendingRole !== null ||
    confirm !== null ||
    payloadError !== null ||
    action.error !== null ||
    action.actionId !== null;

  return (
    <>
      <tr style={revoked ? { opacity: 0.55 } : undefined}>
        <td style={{ ...cellStyle, maxWidth: 280, overflowWrap: "anywhere" }}>
          <div style={{ fontWeight: 600, color: "var(--text)" }}>{user.email ?? "(no email)"}</div>
          {user.userName && user.userName !== user.email ? (
            // user_name is end-user-writable (auth metadata): render as
            // text (React escapes) and wrap so it cannot stretch the table.
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
              {user.userName}
            </div>
          ) : null}
        </td>
        <td style={cellStyle}>
          {revoked || !roleKnown ? (
            <span style={{ color: "var(--text-2)" }}>{user.role ?? "-"}</span>
          ) : (
            <select
              value={pendingRole ?? (user.role as TenantUserRole)}
              onChange={(e) => {
                const next = e.target.value as TenantUserRole;
                setPendingRole(next === user.role ? null : next);
                setConfirm(null);
              }}
              disabled={action.busy}
              style={inlineInputStyle}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          )}
        </td>
        <td style={cellStyle}>{relativeTime(user.lastSignInAt)}</td>
        <td style={cellStyle}>{user.invitedAt ? relativeTime(user.invitedAt) : "-"}</td>
        <td style={cellStyle}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {revoked ? <StatusBadge label="Revoked" tone="red" /> : null}
            {!revoked && user.handoffDoneAt ? (
              <StatusBadge label="Handoff done" tone="green" />
            ) : null}
            {pendingInvite ? <StatusBadge label="Pending" tone="yellow" /> : null}
          </div>
        </td>
        <td style={cellStyle}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {revoked ? (
              user.email && roleKnown ? (
                <button
                  type="button"
                  disabled={action.busy}
                  onClick={() => setConfirm("reinvite")}
                  style={smallSecondaryButtonStyle}
                >
                  Re-invite
                </button>
              ) : null
            ) : roleKnown ? (
              <>
                {!user.handoffDoneAt ? (
                  <button
                    type="button"
                    disabled={action.busy}
                    onClick={() => {
                      setConfirm(null);
                      runRow("tenant_user_handoff_done", { slug, user_id: user.userId });
                    }}
                    style={smallSecondaryButtonStyle}
                  >
                    Mark handoff done
                  </button>
                ) : null}
                {pendingInvite && user.email ? (
                  <button
                    type="button"
                    disabled={action.busy}
                    onClick={() => setConfirm("resend")}
                    style={smallSecondaryButtonStyle}
                  >
                    Resend invite
                  </button>
                ) : null}
                {/* MER-539: available to every active member, including one
                    who has already signed in. That case is the whole point:
                    tenant_user_invite refuses it, so a locked-out member
                    with a dead reset email had no panel path at all. */}
                <button
                  type="button"
                  disabled={action.busy}
                  onClick={() => setConfirm("regenerate")}
                  style={smallSecondaryButtonStyle}
                >
                  Regenerate login link
                </button>
                <button
                  type="button"
                  disabled={action.busy}
                  onClick={() => setConfirm("revoke")}
                  style={dangerOutlineButtonStyle}
                >
                  Revoke
                </button>
              </>
            ) : (
              // staff-viewer (and any future non-panel role) rows are
              // managed on the STAFF_SETUP.md manual path; no actions.
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>manual path</span>
            )}
          </div>
        </td>
      </tr>
      {detailOpen ? (
        <tr>
          <td colSpan={6} style={{ ...cellStyle, background: "rgba(0,0,0,0.02)" }}>
            <div style={{ display: "grid", gap: 8 }}>
              {pendingRole ? (
                <div style={confirmRowStyle}>
                  <span style={{ fontSize: 13, color: "var(--text)" }}>
                    Change {user.email ?? "this user"} to {ROLE_LABELS[pendingRole]}?
                  </span>
                  <button
                    type="button"
                    disabled={action.busy}
                    onClick={() => {
                      const role = pendingRole;
                      setPendingRole(null);
                      runRow("tenant_user_role_set", { slug, user_id: user.userId, role });
                    }}
                    style={smallPrimaryButtonStyle}
                  >
                    Yes, change role
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingRole(null)}
                    style={smallSecondaryButtonStyle}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}
              {confirm === "revoke" ? (
                <div style={confirmRowStyle}>
                  <span style={{ fontSize: 13, color: "var(--text)" }}>
                    Revoke {user.email ?? "this user"} from /{slug}? They lose dashboard access
                    until re-invited.
                  </span>
                  <button
                    type="button"
                    disabled={action.busy}
                    onClick={() => {
                      setConfirm(null);
                      runRow("tenant_user_revoke", { slug, user_id: user.userId });
                    }}
                    style={dangerButtonStyle}
                  >
                    Yes, revoke
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirm(null)}
                    style={smallSecondaryButtonStyle}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}
              {confirm === "resend" ? (
                <div style={confirmRowStyle}>
                  <span style={{ fontSize: 13, color: "var(--text)" }}>
                    Send the invite email to {user.email} again? Any link issued earlier stops
                    working.
                  </span>
                  <button
                    type="button"
                    disabled={action.busy}
                    onClick={() => {
                      setConfirm(null);
                      runRow("tenant_user_invite", {
                        slug,
                        email: user.email,
                        role: user.role,
                      });
                    }}
                    style={smallPrimaryButtonStyle}
                  >
                    Yes, resend
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirm(null)}
                    style={smallSecondaryButtonStyle}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}
              {confirm === "regenerate" ? (
                <div style={confirmRowStyle}>
                  <span style={{ fontSize: 13, color: "var(--text)" }}>
                    Generate a new sign-in link for {user.email ?? "this user"}? No email is sent:
                    the link is shown here once for you to hand over. Any link issued earlier stops
                    working.
                  </span>
                  <button
                    type="button"
                    disabled={action.busy}
                    onClick={() => {
                      setConfirm(null);
                      submitRegenerate();
                    }}
                    style={smallPrimaryButtonStyle}
                  >
                    Yes, generate a link
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirm(null)}
                    style={smallSecondaryButtonStyle}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}
              {confirm === "reinvite" ? (
                <div style={confirmRowStyle}>
                  <span style={{ fontSize: 13, color: "var(--text)" }}>
                    Re-invite {user.email} to /{slug}? This resets the membership to a fresh state;
                    the revocation stays recorded in the activity log below.
                  </span>
                  <button
                    type="button"
                    disabled={action.busy}
                    onClick={() => {
                      setConfirm(null);
                      runRow("tenant_user_invite", {
                        slug,
                        email: user.email,
                        role: user.role,
                        reinvite: true,
                      });
                    }}
                    style={smallPrimaryButtonStyle}
                  >
                    Yes, re-invite
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirm(null)}
                    style={smallSecondaryButtonStyle}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}
              {payloadError ? (
                <div style={{ fontSize: 12, color: "var(--flag-red)" }}>{payloadError}</div>
              ) : null}
              {action.error ? (
                <div style={{ fontSize: 12, color: "var(--flag-red)" }}>{action.error}</div>
              ) : null}
              {action.actionId ? (
                <ActionStatusPanel actionId={action.actionId} onSummary={handleSummary} />
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function InviteBlock(props: {
  slug: string;
  onTerminal: (succeeded: boolean) => void;
  outcomeChannel: OutcomeChannel;
}) {
  const { slug, onTerminal, outcomeChannel } = props;
  const action = useAdminAction();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantUserRole>("viewer");
  const [displayName, setDisplayName] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Captured at submit so the outcome box names the right address
  // even after the form is cleared.
  const [submittedEmail, setSubmittedEmail] = useState("");
  // MER-516: the cross-context guard fails the action on the droplet poller,
  // so its message arrives as a TERMINAL summary.error (via ActionStatusPanel),
  // NOT as the enqueue-time action.error. Track that terminal-failed signal
  // here so the deliberate-bypass "Invite anyway" button can be offered.
  const [guardBlocked, setGuardBlocked] = useState(false);
  const submitEpochRef = useRef(0);

  function submit(confirmUnusual = false) {
    const built = buildInvitePayload({
      slug,
      email,
      role,
      displayName,
      confirmUnusualMember: confirmUnusual,
    });
    if (!built.ok) {
      setFieldErrors(built.errors);
      return;
    }
    setFieldErrors({});
    setGuardBlocked(false);
    setSubmittedEmail(built.payload.email);
    submitEpochRef.current = outcomeChannel.clear();
    void action.run("tenant_user_invite", built.payload);
  }

  function handleSummary(s: ActionSummary) {
    if (!s.terminal) return;
    if (s.status === "succeeded" && action.actionId) {
      const email = submittedEmail;
      const epoch = submitEpochRef.current;
      fetchAdminAction(getSupabase(), action.actionId)
        .then((row) =>
          outcomeChannel.publish(epoch, {
            kind: "invite",
            email,
            summary: summarizeInviteResult(row),
          }),
        )
        .catch(() => {
          // No outcome box; the user shows up in the list, where
          // Regenerate login link issues a fresh one-time link.
        });
      setEmail("");
      setDisplayName("");
      setGuardBlocked(false);
    } else if (s.status === "failed" && s.error?.includes("cross_context_guard")) {
      setGuardBlocked(true);
    }
    onTerminal(s.status === "succeeded");
  }

  return (
    <div style={actionBlockStyle}>
      <div style={actionBlockTitleStyle}>Invite a user</div>
      <div style={actionBlockHintStyle}>
        Sends an invite email when possible. If a one-time sign-in link is generated instead, it is
        shown here exactly once for manual handoff. For someone who is already a member, use
        Regenerate login link on their row instead.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="person@company.com"
          spellCheck={false}
          style={{ ...inlineInputStyle, flex: 1, minWidth: 200, maxWidth: 280 }}
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as TenantUserRole)}
          style={inlineInputStyle}
        >
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Display name (optional)"
          spellCheck={false}
          style={{ ...inlineInputStyle, flex: 1, minWidth: 160, maxWidth: 240 }}
        />
        <button
          type="button"
          disabled={action.busy}
          onClick={() => submit()}
          style={action.busy ? smallPrimaryBusyStyle : smallPrimaryButtonStyle}
        >
          {action.busy ? "Inviting..." : "Invite"}
        </button>
      </div>
      {Object.entries(fieldErrors).map(([field, message]) => (
        <div key={field} style={{ fontSize: 11, color: "var(--flag-red)" }}>
          {message}
        </div>
      ))}
      {action.error ? (
        <div style={{ fontSize: 12, color: "var(--flag-red)" }}>{action.error}</div>
      ) : null}
      {action.actionId ? (
        <ActionStatusPanel actionId={action.actionId} onSummary={handleSummary} />
      ) : null}
      {/* MER-516: the cross-context guard (internal address, or a user already
          on another tenant) reports as a TERMINAL failed action; its message
          renders in the status panel above. Offer a deliberate bypass here;
          guardBlocked is set from that terminal summary, not action.error. */}
      {guardBlocked ? (
        <button
          type="button"
          disabled={action.busy}
          onClick={() => submit(true)}
          style={action.busy ? smallPrimaryBusyStyle : smallPrimaryButtonStyle}
        >
          Invite anyway
        </button>
      ) : null}
    </div>
  );
}

function InviteOutcomeBox(props: {
  email: string;
  summary: InviteResultSummary;
  accessCode?: string | null;
}) {
  const { email, summary, accessCode } = props;
  const hasAccessCode = typeof accessCode === "string" && accessCode.trim() !== "";
  return (
    <div style={outcomeBoxStyle}>
      {summary.emailSent ? (
        <div>
          <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 600 }}>
            Invite email sent to {email}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            If it is not in the inbox, check spam (new sender domain).
          </div>
        </div>
      ) : null}
      {summary.actionLink ? (
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, color: "var(--flag-red)", fontWeight: 600 }}>
            One-time link: copy it now, it is not shown again. Generating a new link invalidates
            this one.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <code style={linkBoxStyle}>{summary.actionLink}</code>
            <CopyButton value={summary.actionLink} />
          </div>
        </div>
      ) : null}
      {!summary.emailSent && !summary.actionLink ? (
        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
          {summary.delivery === "none_existing_user"
            ? `No invite email needed: ${email} already has an account and was added to this customer.`
            : "The invite completed, but no email or link came back. Check the action steps above."}
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {hasAccessCode ? (
          <>
            <span style={{ fontSize: 12, color: "var(--text-2)", fontWeight: 600 }}>
              Portal access code
            </span>
            <code style={codeChipStyle}>{accessCode}</code>
            <CopyButton value={accessCode ?? ""} />
          </>
        ) : (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Portal access code: not set (use the Access code block above)
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * MER-539: the hand-over box for a regenerated sign-in link.
 *
 * Two links can come back and they are not equivalent, so the box is
 * explicit about which to send:
 *
 *   * The app link (/set-password?token_hash=...) verifies only on a
 *     deliberate click, so a mail scanner or a Slack unfurl issuing a
 *     GET cannot spend it in transit. That GET-on-arrival is what made
 *     users report "expired or already used" links (MER-523), and a
 *     staff handoff travels through exactly those channels. Preferred.
 *   * The raw GoTrue link points at /auth/v1/verify, which spends the
 *     token on any GET. Shown only as the fallback for an older GoTrue
 *     that returned no token_hash, with the risk stated.
 *
 * Both are one-time and both are shown exactly once; neither is logged.
 */
function RegenerateOutcomeBox(props: { email: string; summary: RegenerateResultSummary }) {
  const { email, summary } = props;
  // window.location.origin is the dashboard origin this panel is served
  // from, which is the same origin /set-password lives on. Reading it
  // here (rather than shipping a configured host from the executor)
  // keeps the link correct in local dev, preview and prod alike.
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  const appLink = buildSetPasswordLink({
    origin,
    tokenHash: summary.tokenHash,
    linkType: summary.linkType,
  });
  const purpose =
    summary.linkType === "recovery"
      ? "They set a new password at the end of it, so this also clears the lockout."
      : "It completes their invite and lets them set a password.";
  return (
    <div style={outcomeBoxStyle}>
      <div>
        <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 600 }}>
          One-time sign-in link for {email}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
          No email was sent. Send this to them yourself. {purpose}
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--flag-red)", fontWeight: 600 }}>
        Copy it now, it is not shown again. Generating another link invalidates this one.
      </div>
      {appLink ? (
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, color: "var(--text-2)", fontWeight: 600 }}>
            Send this one. It survives mail scanners and link previews, because it only signs them
            in when they click it.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <code style={linkBoxStyle}>{appLink}</code>
            <CopyButton value={appLink} />
          </div>
        </div>
      ) : null}
      {summary.actionLink ? (
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 12, color: "var(--text-2)", fontWeight: 600 }}>
            {appLink
              ? "Direct link (fallback). A scanner or link preview can spend this one before they click it."
              : "Direct link. A scanner or link preview can spend this one before they click it, so tell them to open it promptly."}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <code style={linkBoxStyle}>{summary.actionLink}</code>
            <CopyButton value={summary.actionLink} />
          </div>
        </div>
      ) : null}
      {!appLink && !summary.actionLink ? (
        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
          The action completed but returned no link. Check the action steps above and try again.
        </div>
      ) : null}
    </div>
  );
}

function CopyButton(props: { value: string }) {
  const [copied, setCopied] = useState<"idle" | "copied" | "failed">("idle");
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard
          .writeText(props.value)
          .then(() => setCopied("copied"))
          .catch(() => setCopied("failed"));
      }}
      style={smallSecondaryButtonStyle}
    >
      {copied === "copied" ? "Copied" : copied === "failed" ? "Copy failed" : "Copy"}
    </button>
  );
}

function StatusBadge(props: { label: string; tone: "red" | "green" | "yellow" }) {
  const tones: Record<"red" | "green" | "yellow", { bg: string; fg: string }> = {
    red: { bg: "rgba(192,57,43,0.10)", fg: "var(--flag-red)" },
    green: { bg: "rgba(46,160,67,0.12)", fg: "var(--flag-green)" },
    yellow: { bg: "rgba(255,193,7,0.16)", fg: "var(--flag-yellow)" },
  };
  const tone = tones[props.tone];
  return (
    <span
      style={{
        fontSize: 10,
        padding: "2px 8px",
        borderRadius: 8,
        background: tone.bg,
        color: tone.fg,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        whiteSpace: "nowrap",
      }}
    >
      {props.label}
    </span>
  );
}

// Mirrors of the CustomerDetail.tsx style constants (not exported
// there); values must stay identical so the page reads as one surface.

const panelStyle: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: 18,
  display: "grid",
  gap: 12,
};

const panelTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--text-2)",
};

const cellStyle: CSSProperties = {
  padding: "10px 14px",
  fontSize: 13,
  borderBottom: "1px solid var(--border-subtle)",
  verticalAlign: "top",
};

const headerCellStyle: CSSProperties = {
  textAlign: "left",
  padding: "8px 14px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  borderBottom: "1px solid var(--border-subtle)",
  fontWeight: 700,
};

const actionBlockStyle: CSSProperties = {
  display: "grid",
  gap: 8,
  padding: "12px 14px",
  border: "1px solid var(--border-subtle)",
  borderRadius: 8,
};

const actionBlockTitleStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: "var(--text)",
};

const actionBlockHintStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--text-muted)",
};

const inlineInputStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  padding: "7px 10px",
  borderRadius: 6,
  border: "1px solid var(--border-subtle)",
  background: "#FFFFFF",
  color: "var(--text)",
};

const smallSecondaryButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 600,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--brand-line)",
  background: "transparent",
  color: "var(--brand-deep)",
  cursor: "pointer",
};

const smallPrimaryButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 700,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--brand-deep)",
  background: "var(--brand-deep)",
  color: "#FFFFFF",
  cursor: "pointer",
};

const smallPrimaryBusyStyle: CSSProperties = {
  ...smallPrimaryButtonStyle,
  opacity: 0.7,
  cursor: "wait",
};

const dangerButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 700,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--flag-red)",
  background: "var(--flag-red)",
  color: "#FFFFFF",
  cursor: "pointer",
};

const dangerOutlineButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 700,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--flag-red)",
  background: "transparent",
  color: "var(--flag-red)",
  cursor: "pointer",
};

const confirmRowStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};

const outcomeBoxStyle: CSSProperties = {
  display: "grid",
  gap: 10,
  padding: "12px 14px",
  borderRadius: 8,
  border: "1px solid var(--brand-line)",
  background: "rgba(38,168,159,0.06)",
};

const linkBoxStyle: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--border-subtle)",
  background: "#FFFFFF",
  color: "var(--text)",
  wordBreak: "break-all",
  flex: 1,
};

const codeChipStyle: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
  padding: "4px 8px",
  borderRadius: 6,
  border: "1px solid var(--border-subtle)",
  background: "#FFFFFF",
  color: "var(--text)",
};
